import { memo } from 'react'
import dynamic from 'next/dynamic'
import { DynamicImportPlaceholder } from 'root/src/client/web/base/DynamicImportPlaceholder'
import { getVideoTimestampByUrl } from 'root/src/client/util/getVideoTimestampByUrl'
import { facebookPlayerSrc, tiktokPlayerSrc, twitchPlayerSrc, ytPlayerSrc } from 'root/src/shared/constants/playerUrls'
import useScript from 'root/src/client/web/hooks/useScript'
import { FallbackMediaWrapper } from 'root/src/client/v2/video/components/FallbackMediaWrapper'
import { PlayerError } from 'root/src/client/web/base/Player/PlayerError'
import { PlayerLoader } from 'root/src/client/web/base/Player/PlayerLoader'

const fallbackStyle = {
	height: 284,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
}

const FallbackComponent = () => <div style={fallbackStyle}>No platform match or deliveries</div>

const platformPlayersMap = {
	twitch: dynamic(() => import('./TwitchPlayer'), { loading: () => <DynamicImportPlaceholder /> }),
	youtube: dynamic(() => import('./YTPlayer'), { loading: () => <DynamicImportPlaceholder /> }),
	facebook: dynamic(() => import('./FacebookPlayer'), { loading: () => <DynamicImportPlaceholder /> }),
	tiktok: dynamic(() => import('./TiktokPlayer'), { loading: () => <DynamicImportPlaceholder /> }),
	twitter: dynamic(() => import('./TwitterEmbeddedContent'), { loading: () => <DynamicImportPlaceholder /> }),
}

const PLAYER_SCRIPT_SRC_MAP = {
	youtube: ytPlayerSrc,
	twitch: twitchPlayerSrc,
	facebook: facebookPlayerSrc,
	tiktok: tiktokPlayerSrc,
}

const Player = ({
	projectDeliveries, onChangeItemCallbackArr, autoplay,
	muted, completedDare, className, isViewProjectPage, onPlay,
}) => {
	const platform = projectDeliveries?.platform

	const script = PLAYER_SCRIPT_SRC_MAP[platform]
	const [isLoaded, isError] = useScript(script)

	if (!projectDeliveries) {
		return <FallbackComponent />
	}

	const {
		id, mediaType, timestamp,
		timestampQuery, embeddedContent, params, videoURL,
	} = projectDeliveries

	if (script && isError) {
		return <PlayerError videoURL={videoURL} className={className} />
	}

	if (script && !isLoaded) {
		return <PlayerLoader className={className} />
	}

	// YT
	const start = params?.start || timestamp || getVideoTimestampByUrl(videoURL)

	const PlayerComponent = platformPlayersMap[platform] || FallbackComponent

	return (
		<FallbackMediaWrapper
			videoUrl={videoURL}
			muted={muted}
			autoplay={autoplay}
			onPlay={onPlay}
		>
			<PlayerComponent
				onPlay={onPlay}
				videoId={id}
				autoplay={autoplay}
				start={start}
				timestampQuery={timestampQuery}
				onChangeItemCallbackArr={onChangeItemCallbackArr}
				mediaType={mediaType}
				muted={muted}
				completedDare={completedDare}
				className={className}
				embeddedContent={embeddedContent}
				isViewProjectPage={isViewProjectPage}
				videoURL={videoURL}
			/>
		</FallbackMediaWrapper>
	)
}

export default memo(Player)
